import React, { useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import styled from "styled-components";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

import { State } from "src/state/ducks";
import * as shopActions from "src/state/ducks/shop/actions";
import { ShopBagEntity } from "src/firebase/db/ShopBags";

import StyleConfig from "src/constants/StyleConfig";
import { DefaultPageProps, HistoryProps } from "src/constants/Interface";

import { DefaultLayout } from "src/components/layouts/DefaultLayout";
import Heading from "src/components/atoms/typo/Heading";
import PlainHomeButton from "src/components/atoms/button/PlainHomeButton";
import SpinnerModal from "src/components/organisms/loading/SpinnerModal";
import ShopBagsForm from "src/components/organisms/form/ShopBagsForm";
import ConfirmModal from "src/components/organisms/modal/ConfirmModal";

type OwnProps = {
  id: string;
};

type Props = DefaultPageProps &
  OwnProps &
  HistoryProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const ShopBagEditPage = (props: Props) => {
  const { id, shop, errors, getShop, updateShopBags, isFetching } = props;

  // 初期表示：店舗情報の取得
  useEffect(() => {
    getShop(id);
  }, [id, getShop]);

  // エラー時の確認モーダル
  const onError = useCallback(async () => {
    if (!Object.keys(errors).length) {
      return;
    }
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmModal
          title="入力内容に問題があります"
          contentText="未入力の項目があります"
          closeText="戻る"
          onClose={onClose}
          color="red"
        />
      ),
    });
  }, [errors]);

  // エラー時：エラーメッセージを表示
  useEffect(() => {
    onError();
  }, [errors, onError]);

  // 保存
  const onSave = useCallback(
    (query: ShopBagEntity[]) => {
      updateShopBags({ id, query });
    },
    [id, updateShopBags]
  );

  // 削除確認モーダル
  const onConfirmDelete = useCallback(async (onDelete: Function) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmModal
          title="項目の削除"
          contentText="この項目が削除されますが、よろしいでしょうか？"
          closeText="キャンセル"
          onClose={onClose}
          submitText="削除する"
          onSubmit={async () => {
            onClose();
            onDelete();
          }}
          color="red"
        />
      ),
    });
  }, []);

  return (
    <DefaultLayout {...props}>
      <SpinnerModal isLoading={isFetching} />
      <Container>
        <div className="p-ShopBagEditPage">
          <Heading tag="h1" text={`レジ袋オプションを設定する`} />
          <p className="annotation">
            ※レジ袋のサイズを入力し、レジ袋の価格を設定してください
          </p>
          <ShopBagsForm
            shop={shop}
            errors={errors}
            onSave={onSave}
            onConfirmDelete={onConfirmDelete}
          />

          <div className="item">
            <PlainHomeButton />
          </div>
        </div>
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M20};
  .p-ShopBagEditPage {
    > .item {
      margin: ${StyleConfig.HELPER.M24} auto;
    }

    .annotation {
      font-weight: bold;
      font-size: 14px;
      color: ${StyleConfig.COLOR.PRIMARY};
    }
  }
`;

const mapStateToProps = (state: State) => {
  return {
    shop: state.shop.currentShop.resource,
    isFetching: state.shop.currentShop.isFetching || state.shop.bags.isFetching,
    errors: state.shop.bags.validationErrors,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getShop: (id: string) => dispatch(shopActions.getShop(id)),
  updateShopBags: (payload: { id: string; query: ShopBagEntity[] }) =>
    dispatch(shopActions.updateShopBags(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShopBagEditPage);
